/**
 * HistoryPanel Component
 * 
 * Panel untuk menampilkan history undo/redo:
 * - Snapshot list dengan detail (color, metalness, roughness, opacity)
 * - Jump ke snapshot tertentu
 * - Reset ke default
 * - Clear all history
 */

import React from 'react'

export default function HistoryPanel({
  isOpen,
  onClose,
  snapshotCacheRef,
  snapshotIndexRef,
  restoreMaterialSnapshot,
  setSelectedMaterial,
  setSnapshotTrigger,
}) {
  if (!isOpen) return null

  const snapshots = snapshotCacheRef.current || []
  const currentIndex = snapshotIndexRef.current

  const handleJumpToSnapshot = (snapshot, index) => {
    console.log('🕐 Jumping to snapshot #' + snapshot._serial)

    // Guard check
    if (!snapshot || !snapshot._materialRef) {
      console.error('❌ Cannot restore: invalid snapshot')
      return
    }

    snapshotIndexRef.current = index
    const restored = restoreMaterialSnapshot(snapshot)

    if (restored) {
      // Trigger re-render
      setSelectedMaterial((prev) => {
        if (!prev) return prev
        return { ...prev }
      })
      setSnapshotTrigger((prev) => prev + 1)
      console.log('✅ Snapshot restored successfully to #' + snapshot._serial)
    } else {
      console.error('❌ Failed to restore snapshot')
    }
  }

  const handleResetToDefault = () => {
    const defaultSnapshot = snapshots.find((s) => s._isDefault)
    if (defaultSnapshot) {
      console.log('🔄 Resetting to DEFAULT state')
      snapshotIndexRef.current = 0
      restoreMaterialSnapshot(defaultSnapshot)
      setSelectedMaterial((prev) => ({ ...prev }))
      setSnapshotTrigger((prev) => prev + 1)
    } else {
      console.log('⚠️ No default state found')
    }
  }

  const handleClearHistory = () => {
    if (window.confirm('Clear all history? DEFAULT state will be preserved.')) {
      const defaultSnapshot = snapshots.find((s) => s._isDefault)
      if (defaultSnapshot) {
        snapshotCacheRef.current = [defaultSnapshot]
        snapshotIndexRef.current = 0
      } else {
        snapshotCacheRef.current = []
        snapshotIndexRef.current = -1
      }
      setSnapshotTrigger((prev) => prev + 1)
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: '68px',
        right: '20px',
        width: '300px',
        maxHeight: '500px',
        background: 'linear-gradient(135deg, rgba(20, 20, 30, 0.72) 0%, rgba(10, 10, 15, 0.78) 100%)',
        backdropFilter: 'blur(24px) saturate(190%)',
        WebkitBackdropFilter: 'blur(24px) saturate(190%)',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 16px 48px rgba(0, 0, 0, 0.12)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        border: '0.5px solid rgba(255, 255, 255, 0.18)',
        borderTop: '0.5px solid rgba(255, 255, 255, 0.25)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 999,
        animation: 'slideInFromRight 0.4s cubic-bezier(0.32, 0.72, 0, 1) forwards',
        pointerEvents: 'auto',
        userSelect: 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          📝 History
        </h3>
        <button
          onClick={onClose}
          style={{
            padding: '0',
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            color: 'rgba(255, 255, 255, 0.6)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            e.target.style.color = '#fff'
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent'
            e.target.style.color = 'rgba(255, 255, 255, 0.6)'
          }}
        >
          ×
        </button>
      </div>

      {/* History List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
          pointerEvents: 'auto',
        }}
      >
        {snapshots.length === 0 ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '12px',
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📝</div>
            <div>No history yet</div>
            <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7 }}>
              Make changes to create history
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {snapshots.map((snapshot, index) => {
              const isCurrent = index === currentIndex
              const isPast = index < currentIndex
              const isFuture = index > currentIndex

              return (
                <div
                  key={snapshot._timestamp}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleJumpToSnapshot(snapshot, index)
                  }}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: isCurrent
                      ? 'rgba(59, 130, 246, 0.3)'
                      : isPast
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: isCurrent
                      ? '1px solid rgba(59, 130, 246, 0.5)'
                      : '1px solid transparent',
                    opacity: isFuture ? 0.4 : 1,
                    pointerEvents: 'auto',
                    userSelect: 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.backgroundColor = isPast
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(255, 255, 255, 0.02)'
                    }
                  }}
                >
                  {/* Serial & Status */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '6px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: isCurrent ? '#3b82f6' : 'rgba(255, 255, 255, 0.6)',
                        fontFamily: 'monospace',
                        minWidth: '32px',
                      }}
                    >
                      #{snapshot._serial}
                    </span>
                    <span style={{ fontSize: '16px' }}>
                      {isCurrent ? '👉' : isPast ? '✓' : '○'}
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        color: isCurrent ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                        flex: 1,
                      }}
                    >
                      {snapshot.description}
                    </span>
                  </div>

                  {/* Snapshot Details */}
                  <div
                    style={{
                      fontSize: '9px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: 'monospace',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '4px',
                      marginTop: '6px',
                    }}
                  >
                    {snapshot.colorHex && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: snapshot.colorHex,
                            borderRadius: '2px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                          }}
                        />
                        <span>{snapshot.colorHex}</span>
                      </div>
                    )}
                    {snapshot.metalness !== null && (
                      <div>M: {snapshot.metalness?.toFixed(2)}</div>
                    )}
                    {snapshot.roughness !== null && (
                      <div>R: {snapshot.roughness?.toFixed(2)}</div>
                    )}
                    {snapshot.opacity !== null && snapshot.opacity !== 1 && (
                      <div>O: {snapshot.opacity?.toFixed(2)}</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div
        style={{
          padding: '10px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={handleResetToDefault}
          disabled={snapshots.length === 0}
          style={{
            flex: 1,
            padding: '8px 12px',
            backgroundColor: 'rgba(34, 197, 94, 0.2)',
            color: '#22c55e',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '6px',
            cursor: snapshots.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: '10px',
            fontWeight: 600,
            opacity: snapshots.length > 0 ? 1 : 0.5,
          }}
        >
          🔄 Reset to Default
        </button>
        <button
          onClick={handleClearHistory}
          style={{
            flex: 1,
            padding: '8px 12px',
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '10px',
            fontWeight: 600,
          }}
        >
          🗑️ Clear
        </button>
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            fontSize: '9px',
            color: 'rgba(255, 255, 255, 0.4)',
            fontFamily: 'monospace',
            marginTop: '4px',
          }}
        >
          Ctrl+Z Undo • Ctrl+Y Redo
        </div>
      </div>
    </div>
  )
}

